import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, Check, Settings, Image as ImageIcon, Users, Search,
  Shield, FileText, Lock, Globe, Plus, Trash2, 
  ChevronRight, Save, UserCheck, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useTheme } from './ThemeProvider';
import { GovernanceService } from '../services/GovernanceService';
import { UserService } from '../services/api';
import { type Forum, type Tribe, type UserRank } from '../types';
import { identityData } from '../constants/identities';
import { ImageCropperModal } from './ImageCropperModal';
import { useUserData } from './UserDataContext';
import { getUserAvatar } from '../utils/userUtils';

const SendGroupInviteModal = React.lazy(() => import('./SendGroupInviteModal').then(m => ({ default: m.SendGroupInviteModal })));

interface GroupSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: any; // Forum or Tribe
  type: 'forum' | 'tribe';
  onUpdate: (updatedGroup: any) => void;
}

type Tab = 'general' | 'identities' | 'governance' | 'admin';

export function GroupSettingsModal({
  isOpen,
  onClose,
  group,
  type,
  onUpdate
}: GroupSettingsModalProps) {
  const { theme } = useTheme();
  const { userProfile } = useUserData();
  // University tribes have a fixed governance model: invite-only (Restricted) and
  // no custom tags. Driven by the tribe's type in Firestore, never hardcoded per id.
  const isUniversity = type === 'tribe' && group?.tribeType === 'university';
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [fetchingMembers, setFetchingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const MEMBERS_PER_PAGE = 5;

  // Form State
  const [name, setName] = useState(group.name || group.title || '');
  const [description, setDescription] = useState(group.description || '');
  const [rules, setRules] = useState(group.rules || '');
  // University tribes default to Restricted (invite-only); others honour the saved value.
  const [isPublic, setIsPublic] = useState(isUniversity ? false : group.isPublic !== false);
  // 🛡️ Fix: Check both 'identities' and 'tags' to ensure we capture existing tags
  const [selectedIdentities, setSelectedIdentities] = useState<string[]>(group.identities || group.tags || []);
  const [bannerUrl, setBannerUrl] = useState(group.bannerUrl || group.bannerImage || '');
  const [schoolLogoUrl, setSchoolLogoUrl] = useState(group.schoolLogoUrl || '');
  const [logoUrl, setLogoUrl] = useState(group.image || group.logoUrl || '');
  const [tagSearch, setTagSearch] = useState('');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const schoolLogoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // 🔄 State Sync: Fix stale values if the parent container doesn't unmount the modal
  useEffect(() => {
    if (isOpen && group) {
      setName(group.name || group.title || '');
      setDescription(group.description || '');
      setRules(group.rules || '');
      setIsPublic(isUniversity ? false : group.isPublic !== false);
      setSelectedIdentities(group.identities || group.tags || []);
      setBannerUrl(group.bannerUrl || group.bannerImage || '');
      setSchoolLogoUrl(group.schoolLogoUrl || '');
      setLogoUrl(group.image || group.logoUrl || '');
      setActiveTab('general');
      // Reset member pagination/search when modal re-opens
      setMemberSearch('');
      setMemberPage(1);
      setMembers([]);
    }
  }, [isOpen]);

  // Load members when on Admin tab
  useEffect(() => {
    if (activeTab === 'admin' && members.length === 0 && isOpen) {
      loadMembers();
    }
  }, [activeTab, isOpen]);

  const loadMembers = async () => {
    setFetchingMembers(true);
    try {
      const memberRecords = await GovernanceService.getGroupMembers(type, group.id);
      // Enrich with user names/avatars if possible
      const enrichedMembers = await Promise.all(memberRecords.map(async (m: any) => {
        try {
          const user = await UserService.fetchUserById(m.userId || m.id);
          return { ...m, ...user };
        } catch {
          return m;
        }
      }));
      setMembers(enrichedMembers);
    } catch (error) {
      console.error("Failed to load members:", error);
      toast.error("Failed to load members");
    } finally {
      setFetchingMembers(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setLoading(true);
    try {
      const uploadService = type === 'forum' 
        ? (await import('../services/api')).ForumService 
        : (await import('../services/api')).TribeService;
      
      const url = await uploadService.uploadLogo(group.id, file);
      setLogoUrl(url);
      toast.success("Logo updated!");
    } catch (error: any) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setLoading(false);
    }
    e.target.value = '';
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result as string);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const dataURLToBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleCropSave = async (croppedImageUrl: string) => {
    setLoading(true);
    setCropModalOpen(false);
    try {
      const blob = dataURLToBlob(croppedImageUrl);
      const file = new File([blob], "group-banner.jpg", { type: "image/jpeg" });

      const uploadService = type === 'forum'
        ? (await import('../services/api')).ForumService
        : (await import('../services/api')).TribeService;

      const url = await uploadService.uploadBanner(group.id, file);
      setBannerUrl(url);
      toast.success("Banner updated!");
    } catch (error: any) {
      toast.error(error.message || "Failed to upload banner");
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB'); return; }
    e.target.value = '';
    setLoading(true);
    try {
      const { TribeService } = await import('../services/api');
      const url = await TribeService.uploadSchoolLogo(userProfile?.id?.toString() || '', file);
      setSchoolLogoUrl(url);
      await GovernanceService.updateGroup('tribe', group.id, { schoolLogoUrl: url });
      toast.success("School logo updated!");
    } catch (error: any) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setLoading(false);
    }
  };

  const toggleIdentity = (identity: string) => {
    if (!selectedIdentities.includes(identity) && selectedIdentities.length >= 5) {
      toast.error("Maximum 5 tags allowed");
      return;
    }
    setSelectedIdentities(prev => 
      prev.includes(identity) 
        ? prev.filter(i => i !== identity)
        : [...prev, identity].slice(0, 5)
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updates: any = {
        [type === 'forum' ? 'name' : 'name']: name,
        title: name,
        description,
        rules,
        isPublic,
        identities: selectedIdentities,
        tags: selectedIdentities,
        bannerUrl,
        image: logoUrl,
        // Only include schoolLogoUrl for university tribes
        ...(type === 'tribe' && group.tribeType === 'university' && { schoolLogoUrl }),
      };

      await GovernanceService.updateGroup(type, group.id, updates);
      onUpdate({ ...group, ...updates });
      toast.success("Settings saved!");
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      await GovernanceService.updateMemberRole(type, group.id, userId, newRole);
      toast.success(`Role updated to ${newRole}`);
      setMembers(prev => prev.map(m => (m.id === userId || m.userId === userId) ? { ...m, role: newRole } : m));
    } catch (error: any) {
      toast.error(error.message || "Failed to update role");
    }
  };

  const allIdentities = Object.entries(identityData)
    .filter(([category]) => type === 'tribe' ? category !== 'Ethnic Groups' : true)
    .flatMap(([_, values]) => values);

  const filteredTags = allIdentities.filter(t => 
    t.toLowerCase().includes(tagSearch.toLowerCase()) && !selectedIdentities.includes(t)
  ).slice(0, 10);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-light-pattern dark:bg-[#1a1a1a] rounded-3xl shadow-2xl overflow-hidden flex flex-col h-[85vh]"
          >
            {/* Header */}
            <div className="p-6 border-b border-black/5 dark:border-white/5 bg-light-pattern dark:bg-[#1a1a1a] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-black dark:text-white">Authority Settings</h2>
                  <p className="text-xs text-black/60 dark:text-white/60">Manage your {type} community</p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-black dark:text-white" />
              </motion.button>
            </div>

            {/* Tabs */}
            <div className="flex px-4 py-2 gap-1 border-b border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5 overflow-x-auto no-scrollbar shrink-0">
              {[
                { id: 'general', label: 'General', icon: ImageIcon },
                // Tags tab is hidden for university tribes (no custom tags allowed)
                ...(isUniversity ? [] : [{ id: 'identities', label: 'Tags', icon: Plus }]),
                { id: 'governance', label: 'Governance', icon: Shield },
                { id: 'admin', label: 'Members', icon: Users }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as Tab)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'bg-white dark:bg-[#2a2a2a] text-black dark:text-white shadow-sm' 
                      : 'text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              {activeTab === 'general' && (
                <div className="space-y-6">
                  {/* Community Logo - Hidden for university tribes to avoid confusion with school logo sticker */}
                  {!(type === 'tribe' && group.tribeType === 'university') && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Community Logo</label>
                      <div className="flex items-center gap-4">
                        <div 
                          onClick={() => logoInputRef.current?.click()}
                          className="relative w-20 h-20 rounded-2xl bg-black/5 dark:bg-white/5 border-2 border-dashed border-black/10 dark:border-white/10 overflow-hidden cursor-pointer group hover:border-red-500/50 transition-colors shrink-0"
                        >
                          {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain bg-white p-1" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-black/40 dark:text-white/40">
                              <ImageIcon className="w-6 h-6" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <Plus className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-black/60 dark:text-white/60">Upload a logo for your community. It will be scaled down automatically.</p>
                          <input 
                            type="file" 
                            ref={logoInputRef} 
                            onChange={handleLogoUpload} 
                            className="hidden" 
                            accept="image/*"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Community Banner</label>
                    <div 
                      onClick={() => bannerInputRef.current?.click()}
                      className="relative w-full h-32 rounded-2xl bg-black/5 dark:bg-white/5 border-2 border-dashed border-black/10 dark:border-white/10 overflow-hidden cursor-pointer group hover:border-red-500/50 transition-colors"
                    >
                      {bannerUrl ? (
                        <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-black/40 dark:text-white/40">
                          <ImageIcon className="w-8 h-8 mb-1" />
                          <span className="text-[10px] font-bold">Click to upload banner</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Plus className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={bannerInputRef}
                      onChange={handleBannerUpload}
                      className="hidden"
                      accept="image/*"
                    />
                  </div>

                  {/* School Logo — university tribes only */}
                  {type === 'tribe' && group.tribeType === 'university' && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-3 block">School Logo (Circular Sticker)</label>
                      <div className="flex items-center gap-4">
                        <div
                          onClick={() => schoolLogoInputRef.current?.click()}
                          className="relative w-20 h-20 rounded-xl bg-black/5 dark:bg-white/5 border-2 border-dashed border-black/10 dark:border-white/10 overflow-hidden cursor-pointer group hover:border-blue-500/50 transition-colors shrink-0 flex items-center justify-center"
                        >
                          {schoolLogoUrl ? (
                            <img src={schoolLogoUrl} alt="School logo" className="w-full h-full object-contain" />
                          ) : (
                            <ImageIcon className="w-7 h-7 text-black/30 dark:text-white/30" />
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <Plus className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-black/70 dark:text-white/70 mb-1">
                            {schoolLogoUrl ? 'Change logo' : 'Upload school logo'}
                          </p>
                          <p className="text-xs text-black/40 dark:text-white/40 leading-relaxed">
                            Shows as a tilted circular sticker in the top right of the tribe banner. PNG, JPG, or WEBP.
                          </p>
                          {schoolLogoUrl && (
                            <button
                              onClick={() => { setSchoolLogoUrl(''); GovernanceService.updateGroup('tribe', group.id, { schoolLogoUrl: '' }).catch(() => {}); }}
                              className="mt-2 text-[11px] text-red-500 hover:text-red-600 font-bold"
                            >
                              Remove logo
                            </button>
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        ref={schoolLogoInputRef}
                        onChange={handleSchoolLogoUpload}
                        className="hidden"
                        accept="image/png,image/jpeg,image/webp"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Community Name</label>
                    <Input 
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter community name..."
                      className="bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 h-12 text-lg font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Description</label>
                    <Textarea 
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Tell members what this community is about..."
                      className="bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 min-h-[120px] resize-none"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'identities' && isUniversity && (
                <div className="py-12 text-center">
                  <p className="text-sm text-black/50 dark:text-white/50">Tags are not available for university tribes</p>
                </div>
              )}

              {activeTab === 'identities' && !isUniversity && (
                <div className="space-y-6">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Active Tags (Max 5)</label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedIdentities.map(tag => (
                        <span 
                          key={tag}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-bold shadow-sm"
                        >
                          {tag}
                          <button onClick={() => toggleIdentity(tag)} className="hover:text-black/60">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {selectedIdentities.length === 0 && (
                        <p className="text-sm text-black/40 dark:text-white/40 italic">No tags selected yet.</p>
                      )}
                    </div>

                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Add New Tags</label>
                    <div className="relative">
                      <Input 
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        placeholder="Search for tags..."
                        className="bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 h-10 pl-10"
                      />
                      <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 dark:text-white/40" />
                    </div>
                    
                    <div className="mt-3 flex flex-wrap gap-2">
                      {filteredTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => toggleIdentity(tag)}
                          className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-black dark:text-white"
                        >
                          + {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'governance' && (
                <div className="space-y-6">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Privacy Options</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        disabled={isUniversity}
                        onClick={(e) => { e.preventDefault(); if (!isUniversity) setIsPublic(true); }}
                        className={`group flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all outline-none ${isUniversity ? 'opacity-40 cursor-not-allowed' : ''} ${
                          isPublic
                            ? 'bg-linear-to-r from-emerald-600 to-teal-500 border-transparent text-white shadow-lg shadow-emerald-500/20 scale-[1.02]'
                            : 'border-black/5 dark:border-white/5 bg-black/40 dark:bg-white/5 text-black/40 dark:text-white/40 hover:border-emerald-500/30'
                        }`}
                      >
                        <Globe className={`w-8 h-8 ${isPublic ? 'text-white' : 'group-hover:text-emerald-500'}`} />
                        <div className="text-center">
                          <p className="text-sm font-black uppercase tracking-tight">Public</p>
                          <p className={`text-[10px] font-bold ${isPublic ? 'text-white/80' : 'text-black/40 dark:text-white/40'}`}>Anyone can join</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setIsPublic(false); }}
                        className={`group flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all outline-none ${
                          !isPublic 
                            ? 'bg-linear-to-r from-amber-600 to-orange-500 border-transparent text-white shadow-lg shadow-amber-500/20 scale-[1.02]' 
                            : 'border-black/5 dark:border-white/5 bg-black/40 dark:bg-white/5 text-black/40 dark:text-white/40 hover:border-amber-500/30'
                        }`}
                      >
                        <Lock className={`w-8 h-8 ${!isPublic ? 'text-white' : 'group-hover:text-amber-500'}`} />
                        <div className="text-center">
                          <p className="text-sm font-black uppercase tracking-tight">Restricted</p>
                          <p className={`text-[10px] font-bold ${!isPublic ? 'text-white/80' : 'text-black/40 dark:text-white/40'}`}>Invite Only</p>
                        </div>
                      </button>
                    </div>
                    {isUniversity && (
                      <p className="text-[11px] text-black/50 dark:text-white/50 mt-2 flex items-center gap-1.5">
                        <Lock className="w-3 h-3 shrink-0" />
                        University tribes are invite-only — this can't be changed.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40 mb-2 block">Rules & Guidelines</label>
                    <Textarea 
                      value={rules}
                      onChange={(e) => setRules(e.target.value)}
                      placeholder="Define clear rules for your community members..."
                      className="bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 min-h-[150px] resize-none"
                    />
                  </div>

                  {/* Danger Zone */}
                  {(userProfile?.id === group.ownerId || userProfile?.rank === 'legion') && ( 
                     <div className="pt-6 border-t border-red-500/20">
                        <label className="text-xs font-bold uppercase tracking-wider text-red-500 mb-2 block">Danger Zone</label>
                        <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/10 flex items-center justify-between">
                           <div>
                              <p className="text-sm font-bold text-red-500">Delete {type === 'forum' ? 'Forum' : 'Tribe'}</p>
                              <p className="text-xs text-red-400/80">permanently delete this community and all content</p>
                           </div>
                           <Button
                              onClick={async () => {
                                 const confirmName = window.prompt(`To confirm deletion, type "${group.name || group.title}"`);
                                 if (confirmName === (group.name || group.title)) {
                                    setLoading(true);
                                    try {
                                       const { GovernanceService } = await import('../services/GovernanceService');
                                       const { UserService } = await import('../services/api');
                                       const auth = await import('../services/firebaseConfig').then(m => m.getAuthSafe());
                                       
                                       if (!auth.currentUser) throw new Error("Not authenticated");

                                       if (type === 'forum') {
                                          await GovernanceService.deleteForum(auth.currentUser.uid, group.id);
                                       } else {
                                          await GovernanceService.deleteTribe(auth.currentUser.uid, group.id);
                                       }
                                       toast.success(`${type === 'forum' ? 'Forum' : 'Tribe'} deleted`);
                                       onClose();
                                       // Force refresh or navigation? handled by parent usually or reload
                                       window.location.reload(); 
                                    } catch (e: any) {
                                       toast.error(e.message || "Failed to delete");
                                    } finally {
                                       setLoading(false);
                                    }
                                 } else if (confirmName !== null) {
                                    toast.error("Name mismatch, deletion cancelled");
                                 }
                              }}
                              className="bg-red-500 hover:bg-red-600 text-white"
                           >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                           </Button>
                        </div>
                     </div>
                  )}
                </div>
              )}

              {activeTab === 'admin' && (
                <div className="space-y-4">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40">Manage Members</label>
                    <div className="flex items-center gap-3">
                      {/* Invite: only owner (checked in parent) can always invite; 
                          group-watcher+ can also invite — enforced in TribeGroupScreen.
                          In the settings modal, only show Invite if userProfile is the owner. */}
                      <Button
                        onClick={() => setInviteModalOpen(true)}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-7 text-[10px] px-3 shadow-md border-none"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Invite
                      </Button>
                      <button
                        onClick={() => { setMemberPage(1); loadMembers(); }}
                        className="text-[10px] font-bold text-red-500 hover:underline flex items-center gap-1"
                      >
                        Refresh List
                      </button>
                    </div>
                  </div>

                  {/* 🔒 Governance scope disclaimer */}
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <ShieldAlert className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-snug">
                      Roles assigned here are <strong>group-only</strong> and do not affect a member's global rank or app-wide permissions.
                    </p>
                  </div>

                  {/* Member Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/30 dark:text-white/30" />
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => { setMemberSearch(e.target.value); setMemberPage(1); }}
                      placeholder="Search members by name or @username..."
                      className="w-full pl-8 pr-3 h-9 text-xs rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 outline-none focus:ring-1 focus:ring-red-500/40"
                    />
                  </div>

                  {fetchingMembers ? (
                    <div className="flex flex-col items-center justify-center py-10 text-black/40 dark:text-white/40">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Settings className="w-8 h-8" />
                      </motion.div>
                      <p className="text-xs font-bold mt-2">Fetching member database...</p>
                    </div>
                  ) : (() => {
                    // Filter by search, then paginate
                    const filtered = members.filter(m => {
                      if (!memberSearch.trim()) return true;
                      const q = memberSearch.toLowerCase();
                      return (
                        (m.name || '').toLowerCase().includes(q) ||
                        (m.username || '').toLowerCase().replace('@', '').includes(q.replace('@', ''))
                      );
                    });
                    const paginated = filtered.slice(0, memberPage * MEMBERS_PER_PAGE);
                    const hasMore = paginated.length < filtered.length;

                    return (
                      <div className="space-y-2">
                        {paginated.length === 0 && (
                          memberSearch.trim() ? (
                            <div className="text-center py-6 text-black/40 dark:text-white/40">
                              <p className="text-xs">No members match "{memberSearch}"</p>
                            </div>
                          ) : (
                            <div className="text-center py-10 text-black/40 dark:text-white/40">
                              <Users className="w-12 h-12 mx-auto mb-2 opacity-20" />
                              <p className="text-sm">No members found besides yourself</p>
                            </div>
                          )
                        )}

                        {paginated.map(member => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={getUserAvatar(member)}
                                className="w-10 h-10 rounded-full border border-black/10"
                                alt={member.name}
                              />
                              <div>
                                <p className="text-sm font-bold text-black dark:text-white">{member.name || 'Member'}</p>
                                <p className="text-[10px] text-black/60 dark:text-white/60">@{member.username ? member.username.replace(/^@/, '') : 'unknown'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={member.role || 'member'}
                                onChange={(e) => handleUpdateRole(member.userId || member.id, e.target.value as UserRank)}
                                disabled={member.userId === group.ownerId}
                                className={`text-[10px] font-bold rounded-lg border border-black/10 dark:border-white/20 px-2 py-1 outline-none ${
                                  member.userId === group.ownerId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/5'
                                } text-black dark:text-white bg-transparent appearance-auto`}
                              >
                                <option value="member" className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white">Member</option>
                                <option value="moderator" className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white">Moderator</option>
                                <option value="watcher" className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white">Watcher (Group)</option>
                                <option value="union" className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white">Union (Group)</option>
                                <option value="legion" className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white">Admin (Group)</option>
                              </select>
                            </div>
                          </div>
                        ))}

                        {/* Load More */}
                        {hasMore && (
                          <button
                            onClick={() => setMemberPage(p => p + 1)}
                            className="w-full py-2.5 text-xs font-bold text-red-500 hover:text-red-600 border border-red-500/20 hover:border-red-500/40 rounded-xl transition-colors"
                          >
                            Load More ({filtered.length - paginated.length} remaining)
                          </button>
                        )}

                        {members.length > 0 && !fetchingMembers && (
                          <p className="text-center text-[10px] text-black/30 dark:text-white/30 pt-1">
                            Showing {paginated.length} of {filtered.length}{memberSearch ? ` matching "${memberSearch}"` : ''} member{filtered.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-black/5 dark:border-white/5 bg-light-pattern dark:bg-[#1a1a1a] flex gap-3 shrink-0">
              <Button
                onClick={onClose}
                variant="ghost"
                className="flex-1 h-12 text-sm font-bold rounded-2xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded-2xl shadow-lg shadow-red-500/20"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </motion.div>

          <ImageCropperModal
            isOpen={cropModalOpen}
            onClose={() => setCropModalOpen(false)}
            imageUrl={cropImage || ''}
            onSave={handleCropSave}
            aspectRatio={4}
            outputWidth={1600}
            title="Adjust Community Banner"
            description="Fit the image into the 1200x300 frame"
            circular={false}
          />
          <React.Suspense fallback={null}>
            <SendGroupInviteModal
               isOpen={inviteModalOpen}
               onClose={() => setInviteModalOpen(false)}
               group={{
                 id: group.id,
                 name: group.name || group.title || '',
                 bannerUrl: bannerUrl || '',
                 isPublic: isPublic
               }}
               type={type}
            />
          </React.Suspense>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
